import { useEffect, useRef, useState } from "react";
import { Play, Pause, Music2, VolumeX } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { TAALS, getTaal } from "@/lib/taals";
import { TANPURA_KEYS } from "@/lib/notes";
import {
    getCtx,
    playBol,
    preloadTablaSamples,
    startTanpura,
    stopSource,
} from "@/lib/audio";

// Reference key of the recorded tabla bol samples (approx. C dayan).
// We pitch-shift all bols by (scale - reference) semitones to match
// the selected Sa, the way an electronic tabla machine tunes its dayan.
const TABLA_REF_KEY_IDX = 0; // C

export default function Taals() {
    const [taalId, setTaalId] = useState("teentaal");
    const [bpm, setBpm] = useState(80);
    const [playing, setPlaying] = useState(false);
    const [currentBeat, setCurrentBeat] = useState(-1);
    const [scaleIdx, setScaleIdx] = useState(7); // default G
    const [tanpuraOn, setTanpuraOn] = useState(true);

    const sched = useRef({ nextTime: 0, beat: 0, intervalId: null });
    const tanpuraSrcRef = useRef(null);
    const tanpuraReqIdRef = useRef(0);
    const taal = getTaal(taalId);
    const scaleSemitones = scaleIdx - TABLA_REF_KEY_IDX;

    // Preload all tabla samples once
    useEffect(() => {
        preloadTablaSamples().catch(() => {});
    }, []);

    // Tabla scheduler
    useEffect(() => {
        if (!playing) {
            if (sched.current.intervalId) clearInterval(sched.current.intervalId);
            sched.current.intervalId = null;
            setCurrentBeat(-1);
            return;
        }
        const ctx = getCtx();
        const spb = 60 / bpm;
        sched.current.nextTime = ctx.currentTime + 0.08;
        sched.current.beat = 0;

        const scheduler = () => {
            const lookahead = ctx.currentTime + 0.25;
            while (sched.current.nextTime < lookahead) {
                const idx = sched.current.beat % taal.matras;
                const b = taal.beats[idx];
                const accent = b.type === "sam" || b.type === "tali";
                playBol(b.bol, sched.current.nextTime, accent, scaleSemitones);
                const at = (sched.current.nextTime - ctx.currentTime) * 1000;
                setTimeout(() => setCurrentBeat(idx), Math.max(0, at));
                sched.current.beat += 1;
                sched.current.nextTime += spb;
            }
        };
        scheduler();
        const id = setInterval(scheduler, 80);
        sched.current.intervalId = id;
        return () => clearInterval(id);
    }, [playing, taalId, bpm, taal, scaleSemitones]);

    // Tanpura accompaniment: start/stop/key change
    useEffect(() => {
        const reqId = ++tanpuraReqIdRef.current;
        const shouldPlay = playing && tanpuraOn;
        // Always stop any previous tanpura first
        if (tanpuraSrcRef.current) {
            stopSource(tanpuraSrcRef.current);
            tanpuraSrcRef.current = null;
        }
        if (!shouldPlay) return;
        const note = TANPURA_KEYS[scaleIdx].note;
        startTanpura(note, 0.55).then((src) => {
            if (tanpuraReqIdRef.current !== reqId) {
                stopSource(src);
                return;
            }
            tanpuraSrcRef.current = src;
        }).catch(() => {});
        return () => {
            if (tanpuraSrcRef.current) {
                stopSource(tanpuraSrcRef.current);
                tanpuraSrcRef.current = null;
            }
        };
    }, [playing, tanpuraOn, scaleIdx]);

    // Group beats into vibhags for rendering
    const vibhagGroups = [];
    let p = 0;
    taal.vibhag.forEach((vlen) => {
        vibhagGroups.push(taal.beats.slice(p, p + vlen).map((b, i) => ({ ...b, _idx: p + i })));
        p += vlen;
    });

    return (
        <div data-testid="taals-page" className="space-y-10">
            <header>
                <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-3">
                    Tool · 02
                </div>
                <h1 className="font-sans font-extrabold text-5xl lg:text-6xl tracking-tight">
                    Taals
                </h1>
                <p className="text-muted-foreground mt-3 max-w-2xl">
                    {taal.matras} matras · {taal.vibhag.join(" + ")} · Pick a scale to tune both the tabla dayan and the tanpura drone — just like an electronic Sangat.
                </p>
            </header>

            {/* Taal selector */}
            <div className="flex flex-wrap gap-2">
                {TAALS.map((t) => (
                    <button
                        key={t.id}
                        data-testid={`taal-select-${t.id}`}
                        onClick={() => {
                            setPlaying(false);
                            setTaalId(t.id);
                        }}
                        className={`px-4 h-10 rounded-md text-sm font-medium border transition-colors ${
                            t.id === taalId
                                ? "bg-foreground text-background border-foreground"
                                : "bg-secondary/40 border-border text-muted-foreground hover:text-foreground hover:border-foreground/40"
                        }`}
                    >
                        {t.name}
                        <span className="ml-2 font-mono text-[10px] opacity-70">
                            {t.matras}
                        </span>
                    </button>
                ))}
            </div>

            {/* Scale selector — tunes the TABLA dayan AND tanpura together */}
            <div className="rounded-lg border border-border bg-card p-6">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                    <div className="flex items-center gap-3">
                        <Music2 size={16} className="text-primary" />
                        <div>
                            <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
                                
                            </div>
                            <div className="font-bold text-5xl">
                                {TANPURA_KEYS[scaleIdx].label}
                            </div>
                        </div>
                    </div>
                    <button
                        data-testid="taals-tanpura-toggle"
                        onClick={() => setTanpuraOn((v) => !v)}
                        className={`flex items-center gap-2 px-4 h-10 rounded-md border text-sm font-medium transition-colors ${
                            tanpuraOn
                                ? "bg-primary/10 border-primary text-primary"
                                : "bg-secondary/40 border-border text-muted-foreground hover:text-foreground"
                        }`}
                    >
                        {tanpuraOn ? (
                            <>
                                <Music2 size={14} /> Tanpura drone on
                            </>
                        ) : (
                            <>
                                <VolumeX size={14} /> Tanpura drone off
                            </>
                        )}
                    </button>
                </div>
                <div className="grid grid-cols-6 sm:grid-cols-12 gap-2">
                    {TANPURA_KEYS.map((k, i) => {
                        const active = i === scaleIdx;
                        return (
                            <button
                                key={k.note}
                                data-testid={`taals-scale-${k.note}`}
                                onClick={() => setScaleIdx(i)}
                                className={`h-12 rounded-md border font-bold text-base transition-all ${
                                    active
                                        ? "bg-primary text-primary-foreground border-primary"
                                        : "bg-secondary/40 border-border text-foreground hover:border-primary/50"
                                }`}
                            >
                                {k.note}
                            </button>
                        );
                    })}
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                    Tabla dayan is tuned to <span className="font-mono text-foreground">{TANPURA_KEYS[scaleIdx].note}</span> · Sa matches the tanpura drone — just like a real electronic tabla machine.
                </p>
            </div>

            {/* Beat grid */}
            <div className="rounded-lg border border-border bg-card p-6 lg:p-10">
                <div className="flex items-baseline justify-between mb-6">
                    <div>
                        <div className="font-bold text-3xl">{taal.name}</div>
                        <div className="text-sm text-muted-foreground mt-1">
                            {taal.description}
                        </div>
                    </div>
                    <div className="hidden sm:flex font-mono text-xs uppercase tracking-widest text-muted-foreground gap-4">
                        <span className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-primary" />
                            Sam
                        </span>
                        <span className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-accent" />
                            Tali
                        </span>
                        <span className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-muted-foreground" />
                            Khali
                        </span>
                    </div>
                </div>

                <div className="flex flex-wrap gap-x-6 gap-y-4">
                    {vibhagGroups.map((group, gi) => (
                        <div key={gi} className="flex gap-2">
                            {group.map((b) => {
                                const active = b._idx === currentBeat;
                                const typeColor =
                                    b.type === "sam"
                                        ? "border-primary text-primary"
                                        : b.type === "tali"
                                          ? "border-accent text-accent"
                                          : b.type === "khali"
                                            ? "border-muted-foreground text-muted-foreground"
                                            : "border-border text-foreground";
                                return (
                                    <div
                                        key={b._idx}
                                        data-testid={`taal-beat-${b._idx}`}
                                        className={`w-16 h-20 rounded-md border-2 ${typeColor} flex flex-col items-center justify-center transition-all ${
                                            active
                                                ? "bg-primary/10 scale-110 shadow-md"
                                                : ""
                                        }`}
                                    >
                                        <div className="font-mono text-[10px] uppercase opacity-70">
                                            {b._idx + 1}
                                        </div>
                                        <div className="font-bold text-lg leading-tight mt-1">
                                            {b.bol}
                                        </div>
                                        {b.type && (
                                            <div className="font-mono text-[9px] uppercase mt-1 opacity-80">
                                                {b.type === "sam"
                                                    ? "×"
                                                    : b.type === "khali"
                                                      ? "0"
                                                      : "•"}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>

            {/* Controls */}
            <div className="grid sm:grid-cols-2 gap-6">
                <div className="rounded-lg border border-border bg-card p-6">
                    <div className="flex items-center justify-between mb-3">
                        <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
                            Tempo
                        </label>
                        <span
                            data-testid="taals-bpm-value"
                            className="font-mono text-2xl"
                        >
                            {bpm} <span className="text-xs">BPM</span>
                        </span>
                    </div>
                    <Slider
                        data-testid="taals-bpm-slider"
                        value={[bpm]}
                        onValueChange={([v]) => setBpm(v)}
                        min={40}
                        max={240}
                        step={1}
                    />
                </div>
                <button
                    data-testid="taals-play-button"
                    onClick={() => setPlaying((p) => !p)}
                    className={`h-full min-h-[88px] rounded-lg border font-medium text-base flex items-center justify-center gap-2 transition-all ${
                        playing
                            ? "bg-foreground text-background border-foreground"
                            : "bg-primary text-primary-foreground border-primary hover:-translate-y-0.5"
                    }`}
                >
                    {playing ? (
                        <>
                            <Pause size={20} /> Stop
                        </>
                    ) : (
                        <>
                            <Play size={20} /> Play {taal.name}
                            {tanpuraOn ? ` + Tanpura ${TANPURA_KEYS[scaleIdx].note}` : ""}
                        </>
                    )}
                </button>
            </div>
        </div>
    );
}
